import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Scale, Shield, Users, ChevronRight, Check, Car, Stethoscope, Calendar, MapPin, User, Phone, Mail, FileText, ArrowRight, LogIn, Wallet, TrendingUp, ClipboardCheck, PhoneCall, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import axios from 'axios';
import { apiUrl, formatApiErrorDetail } from '@/lib/apiBase';
import {
  saveLeadSession,
  clearLeadSession,
  buildLeadSessionHeaders,
  formatLeadSessionError,
} from '@/lib/leadSession';
import { uploadLeadDocuments } from '@/lib/r2Upload';
import { SeoHead, SEO_KEYWORDS_PRIMARY } from '../components/SeoHead';

const INCIDENT_TYPES = [
  { value: 'stradale', label: 'Incidente Stradale', icon: Car, description: 'Sinistri stradali, tamponamenti, investimenti' },
  { value: 'malasanita', label: 'Malasanità', icon: Stethoscope, description: 'Errori medici, negligenza sanitaria' }
];

const PHONE_ALLOWED_CHARS = /[^+0-9\s()-]/g;
const QUESTION_LABEL_STYLE = { color: '#FFFFFF', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.03em' };

export function LandingPage() {
  const navigate = useNavigate();
  const formSectionRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [createdLeadId, setCreatedLeadId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFilesCount, setUploadedFilesCount] = useState(0);
  
  const [formData, setFormData] = useState({
    tipo_incidente: '',
    responsabilita: '',
    stradale_tipo_incidente: '',
    stradale_lesioni: '',
    stradale_prognosi: '',
    stradale_prove: '',
    stradale_lavoro: '',
    stradale_danni_mezzo: '',
    luogo: '',
    nome: '',
    cognome: '',
    telefono: '',
    email: '',
    descrizione: '',
    malasanita_tipo_errore: '',
    malasanita_gravita_conseguenze: '',
    malasanita_documenti_medici: '',
    malasanita_esito_post_trattamento: '',
    malasanita_impatto_vita_lavoro: '',
    malasanita_autorizzazione_medico_legale: '',
    evento_malasanita: '',
    cartella_clinica_richiesta: '',
    invalidita_o_decesso: '',
    struttura_coinvolta: ''
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const sanitizePhone = (value) => value.replace(PHONE_ALLOWED_CHARS, '');
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.tipo_incidente !== '';
      case 2:
        if (formData.tipo_incidente === 'malasanita') {
          return (
            formData.malasanita_tipo_errore !== '' &&
            formData.malasanita_gravita_conseguenze !== '' &&
            formData.malasanita_documenti_medici !== '' &&
            formData.malasanita_esito_post_trattamento !== '' &&
            formData.malasanita_impatto_vita_lavoro !== '' &&
            formData.malasanita_autorizzazione_medico_legale !== ''
          );
        }
        return (
          formData.responsabilita !== '' &&
          formData.stradale_tipo_incidente !== '' &&
          formData.stradale_lesioni !== '' &&
          formData.stradale_prognosi !== '' &&
          formData.stradale_prove !== '' &&
          formData.stradale_lavoro !== '' &&
          formData.stradale_danni_mezzo !== ''
        );
      case 3:
        return true;
      case 4:
        return (
          formData.nome !== '' &&
          formData.telefono !== '' &&
          formData.email !== ''
        );
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  useEffect(() => {
    const el = formSectionRef.current;
    const headerOffset = 96;
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const computeLeadScore = () => {
    if (formData.tipo_incidente === 'malasanita') {
      let score = 0;
      if (formData.malasanita_tipo_errore === 'errore_chirurgico') score += 25;
      else if (formData.malasanita_tipo_errore === 'errore_diagnostico') score += 20;
      else if (formData.malasanita_tipo_errore === 'infezione_ospedaliera') score += 15;

      if (formData.malasanita_gravita_conseguenze === 'decesso_paziente') score += 30;
      else if (formData.malasanita_gravita_conseguenze === 'danno_permanente') score += 25;
      else if (formData.malasanita_esito_post_trattamento === 'reintervento_peggioramento') score += 18;

      if (formData.malasanita_documenti_medici === 'cartella_clinica_completa') score += 20;
      else if (['referti_ed_esami', 'perizia_medico_legale'].includes(formData.malasanita_documenti_medici)) score += 15;

      if (formData.malasanita_esito_post_trattamento === 'reintervento_peggioramento') score += 15;
      else if (formData.malasanita_esito_post_trattamento === 'infezione_complicazioni') score += 10;

      if (formData.malasanita_impatto_vita_lavoro === 'incapacita_totale') score += 10;
      else if (formData.malasanita_impatto_vita_lavoro === 'incapacita_parziale') score += 6;

      if (formData.malasanita_autorizzazione_medico_legale === 'si_autorizzo') score += 5;
      return Math.max(0, Math.min(100, score));
    }

    let score = 0;
    if (formData.responsabilita === 'ragione_piena') score += 30;
    else if (formData.responsabilita === 'concorso_colpa') score += 15;

    if (formData.stradale_lesioni === 'gravi') score += 25;
    else if (formData.stradale_lesioni === 'moderate') score += 18;
    else if (formData.stradale_lesioni === 'lievi') score += 10;

    if (formData.stradale_prognosi === 'gt_30gg') score += 20;
    else if (formData.stradale_prognosi === '10_30gg') score += 15;
    else if (formData.stradale_prognosi === 'lt_10gg') score += 8;

    if (formData.stradale_prove === 'entrambi') score += 15;
    else if (formData.stradale_prove === 'verbale_polizia') score += 12;
    else if (formData.stradale_prove === 'testimoni') score += 8;

    if (formData.stradale_lavoro === 'incapacita_totale') score += 10;
    else if (formData.stradale_lavoro === 'incapacita_parziale') score += 6;

    if (formData.stradale_danni_mezzo === 'grave') score += 5;
    else if (formData.stradale_danni_mezzo === 'medio') score += 3;
    return Math.max(0, Math.min(100, score));
  };

  const handleSubmit = async () => {
    const nome = String(formData.nome || '').trim();
    const telefono = String(formData.telefono || '').trim();
    const email = String(formData.email || '').trim();

    if (!nome || !telefono || !email) {
      setError('Compila nome, telefono ed email prima di inviare.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const lead_score = computeLeadScore();
      const dettagliCategoria =
        formData.tipo_incidente === 'malasanita'
          ? `MALASANITA\nTipo errore: ${formData.malasanita_tipo_errore}\nGravità conseguenze: ${formData.malasanita_gravita_conseguenze}\nDocumenti medici: ${formData.malasanita_documenti_medici}\nDopo il trattamento: ${formData.malasanita_esito_post_trattamento}\nImpatto vita/lavoro: ${formData.malasanita_impatto_vita_lavoro}\nAutorizzazione medico legale: ${formData.malasanita_autorizzazione_medico_legale}`
          : `INCIDENTE STRADALE\nResponsabilità: ${formData.responsabilita}\nTipo incidente: ${formData.stradale_tipo_incidente}\nLesioni: ${formData.stradale_lesioni}\nPrognosi: ${formData.stradale_prognosi}\nProve: ${formData.stradale_prove}\nLavoro: ${formData.stradale_lavoro}\nDanni mezzo: ${formData.stradale_danni_mezzo}`;

      const payload = {
        tipo_incidente: formData.tipo_incidente,
        // Campi richiesti dal backend: impostati con default coerenti.
        data_sinistro: new Date().toISOString().slice(0, 10),
        gravita_danni: 'media',
        ha_avvocato: 'no',
        luogo: formData.luogo || 'non_specificato',
        documenti_disponibili: false,
        responsabile_identificato: false,
        nome,
        cognome: formData.cognome,
        telefono,
        email,
        descrizione: `${dettagliCategoria}\nLead Score (pre-submit): ${lead_score}`,
      };
      const { data } = await axios.post(apiUrl('/api/leads'), payload, { withCredentials: false });
      const newLeadId = data?.id || '';
      const sessionToken = data?.lead_session_token || '';
      saveLeadSession(newLeadId, sessionToken);
      setCreatedLeadId(newLeadId);
      const sessionHeaders = buildLeadSessionHeaders(newLeadId);
      if (formData.tipo_incidente === 'malasanita') {
        const qualificationPayload = {
          malasanita_tipo_errore: formData.malasanita_tipo_errore,
          malasanita_gravita_conseguenze: formData.malasanita_gravita_conseguenze,
          malasanita_documenti_medici: formData.malasanita_documenti_medici,
          malasanita_esito_post_trattamento: formData.malasanita_esito_post_trattamento,
          malasanita_impatto_vita_lavoro: formData.malasanita_impatto_vita_lavoro,
          malasanita_autorizzazione_medico_legale: formData.malasanita_autorizzazione_medico_legale,
          // Retrocompatibilità backend storico
          evento_malasanita: formData.malasanita_tipo_errore,
          cartella_clinica_richiesta: formData.malasanita_documenti_medici === 'nulla' ? 'no' : 'si',
          invalidita_o_decesso: ['danno_permanente', 'decesso_paziente'].includes(formData.malasanita_gravita_conseguenze) ? 'si' : 'no',
          struttura_coinvolta: 'non_specificata',
          // Retrocompatibilità backend
          esito_evento: formData.malasanita_tipo_errore,
        };
        await axios.patch(
          apiUrl(`/api/leads/${newLeadId}/qualifica`),
          qualificationPayload,
          { headers: sessionHeaders, withCredentials: false }
        );
      } else {
        const qualificationPayload = {
          responsabilita: formData.responsabilita,
          stradale_tipo_incidente: formData.stradale_tipo_incidente,
          stradale_lesioni: formData.stradale_lesioni,
          stradale_prognosi: formData.stradale_prognosi,
          stradale_prove: formData.stradale_prove,
          stradale_lavoro: formData.stradale_lavoro,
          stradale_danni_mezzo: formData.stradale_danni_mezzo,
        };
        await axios.patch(
          apiUrl(`/api/leads/${newLeadId}/qualifica`),
          qualificationPayload,
          { headers: sessionHeaders, withCredentials: false }
        );
      }
      if (selectedFiles.length > 0) {
        const uploaded = await uploadLeadDocuments({
          leadId: newLeadId,
          files: selectedFiles,
          sessionHeaders,
        });
        setUploadedFilesCount(uploaded || selectedFiles.length);
      } else {
        setUploadedFilesCount(0);
      }
      clearLeadSession();
      navigate('/thank-you');
    } catch (err) {
      const sessionErr = formatLeadSessionError(err);
      setError(
        sessionErr
          || formatApiErrorDetail(err.response?.data?.detail)
          || err.message
          || 'Si è verificato un errore durante l\'invio.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = (currentStep / 4) * 100;
  const isCurrentStepReady = validateStep(currentStep);

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: '#0A192F' }}>
      <SeoHead
        title="Lead Legale | Infortunistica stradale, risarcimento danni e consulenza legale"
        description="Valutazione professionale per infortunistica stradale e malasanità. Consulenza legale e supporto per il risarcimento danni. Richiedi assistenza o scopri i lead qualificati per studi legali."
        keywords={SEO_KEYWORDS_PRIMARY}
        path="/"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-3" style={{ backgroundColor: 'rgba(30, 41, 59, 0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scale className="w-8 h-8" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
            <span className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>LeadLegale</span>
          </div>
          <Link to="/login" data-testid="header-login-btn">
            <Button variant="ghost" className="flex items-center gap-2" style={{ color: '#A0ABC0' }}>
              <LogIn className="w-4 h-4" strokeWidth={1.5} />
              Accesso Studi Partner
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section - Above the Fold */}
      <section
        className="hero-section min-h-screen relative"
        role="img"
        aria-label="Consulenza legale e infortunistica stradale: studio legale professionale per risarcimento danni"
      >
        <div className="hero-overlay absolute inset-0" aria-hidden="true" />
        <div className="relative z-10 mx-auto px-4 sm:px-6 w-full" style={{ maxWidth: '1200px' }}>
          <div className="min-h-screen pt-16 sm:pt-20 lg:pt-20 pb-8 sm:pb-10">
            <div className="flex flex-col md:flex-row gap-6 lg:gap-10 items-start w-full">
            {/* Left - Hero Content */}
            <div className="animate-fade-in pt-0 sm:pt-1 lg:pt-2 md:flex-1">
              <Scale className="w-12 h-12 sm:w-14 sm:h-14 mb-3 sm:mb-4" style={{ color: '#D4AF37' }} strokeWidth={1.4} />
              <h1 className="text-2xl sm:text-3xl lg:text-6xl tracking-tighter font-medium leading-tight mb-2 sm:mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
                <span className="sm:hidden">Ottieni il tuo risarcimento con supporto legale.</span>
                <span className="hidden sm:inline">
                  Il Futuro dell&apos;Infortunistica:{' '}
                  <span className="text-gold-gradient">Lead Qualificati</span>,{' '}
                  Risultati Garantiti
                </span>
              </h1>
              <p className="text-xs sm:text-base mb-2 sm:mb-3" style={{ color: 'rgba(212, 175, 55, 0.95)', fontFamily: 'Outfit, sans-serif' }}>
                Smettila di inseguire i clienti. Ricevi solo casi pre-qualificati pronti per essere firmati.
              </p>
              <p className="text-sm sm:text-lg mb-3 sm:mb-6" style={{ color: '#A0ABC0', fontFamily: 'Outfit, sans-serif' }}>
                Il nostro sistema di filtraggio automatico seleziona solo i casi con alto potenziale di successo, 
                permettendoti di concentrarti sui clienti che contano davvero.
              </p>
              <div className="flex flex-wrap gap-4 sm:gap-6">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                  <span style={{ color: '#F8F9FA' }}>Lead Verificati</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                  <span style={{ color: '#F8F9FA' }}>+500 Studi Partner</span>
                </div>
              </div>
            </div>

            {/* Right - Multi-step Form */}
            <div
              ref={formSectionRef}
              className="glass-card p-4 sm:p-7 lg:p-8 animate-slide-in w-full md:flex-1"
              style={{
                animationDelay: '0.2s',
                border: '1px solid rgba(212, 175, 55, 0.35)',
                boxShadow: '0 20px 45px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(212, 175, 55, 0.08) inset',
              }}
            >
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(34, 197, 94, 0.15)' }}>
                    <Check className="w-8 h-8" style={{ color: '#22C55E' }} />
                  </div>
                  <h3 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
                    Richiesta Inviata!
                  </h3>
                  <p style={{ color: '#A0ABC0' }}>
                    Un nostro consulente ti contatterà entro 24 ore per valutare il tuo caso.
                  </p>
                </div>
              ) : (
                <>
                  {/* Progress Bar */}
                  <div className="mb-6">
                    <div className="flex justify-between mb-3">
                      {[1, 2, 3, 4].map((step) => (
                        <div key={step} className="flex items-center">
                          <div 
                            className={`step-indicator ${currentStep === step ? 'active' : currentStep > step ? 'completed' : 'inactive'}`}
                            data-testid={`step-indicator-${step}`}
                          >
                            {currentStep > step ? <Check className="w-5 h-5" /> : step}
                          </div>
                          {step < 4 && (
                            <div className="w-16 sm:w-24 h-px mx-2" style={{ backgroundColor: currentStep > step ? '#D4AF37' : 'rgba(255,255,255,0.1)' }}></div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="progress-bar">
                      <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
                    </div>
                  </div>

                  <div>
                    {/* Step 1: Tipo Incidente */}
                    {currentStep === 1 && (
                      <div className="animate-fade-in" data-testid="form-step-1">
                        <h3 className="text-xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
                          Che tipo di incidente hai subito?
                        </h3>
                        <RadioGroup
                          value={formData.tipo_incidente}
                          onValueChange={(value) => updateFormData('tipo_incidente', value)}
                          className="space-y-4"
                        >
                          {INCIDENT_TYPES.map((type) => {
                            const isSelected = formData.tipo_incidente === type.value;
                            return (
                            <label
                              key={type.value}
                              className={`flex items-start gap-4 p-4 rounded-lg cursor-pointer transition-all ${isSelected ? 'ring-1 ring-yellow-400' : ''}`}
                              style={{ 
                                backgroundColor: isSelected ? 'rgba(255, 215, 0, 0.16)' : '#112240',
                                border: `1px solid ${isSelected ? 'rgba(255, 215, 0, 0.75)' : 'rgba(255,255,255,0.1)'}`
                              }}
                              data-testid={`incident-type-${type.value}`}
                            >
                              <RadioGroupItem value={type.value} id={type.value} className="mt-1" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <type.icon className="w-6 h-6 shrink-0" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                                  <span className="font-medium" style={{ color: '#F8F9FA' }}>{type.label}</span>
                                </div>
                                <p className="text-sm" style={{ color: '#A0ABC0' }}>{type.description}</p>
                              </div>
                            </label>
                          );
                          })}
                        </RadioGroup>
                      </div>
                    )}

                    {/* Step 2: Domande per categoria */}
                    {currentStep === 2 && (
                      <div className="animate-fade-in space-y-8" data-testid="form-step-2">
                        {formData.tipo_incidente === 'malasanita' ? (
                          <>
                            <h3 className="text-xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
                              Dettagli Malasanità
                            </h3>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Quale tipo di errore ritieni possa aver causato il danno?
                              </Label>
                              <RadioGroup
                                value={formData.malasanita_tipo_errore}
                                onValueChange={(value) => updateFormData('malasanita_tipo_errore', value)}
                                className="space-y-3"
                              >
                                {[
                                  { value: 'errore_chirurgico', label: 'Errore Chirurgico' },
                                  { value: 'errore_diagnostico', label: 'Errore diagnostico' },
                                  { value: 'infezione_ospedaliera', label: 'Infezione ospedaliera' },
                                  { value: 'non_sicuro', label: 'Non sono sicuro' },
                                ].map((opt) => (
                                  <label
                                    key={opt.value}
                                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
                                    style={{
                                      backgroundColor: formData.malasanita_tipo_errore === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240',
                                      border: `1px solid ${formData.malasanita_tipo_errore === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                                    }}
                                  >
                                    <RadioGroupItem value={opt.value} id={`malasanita-tipo-errore-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Qual è la gravità delle conseguenze subite?
                              </Label>
                              <RadioGroup
                                value={formData.malasanita_gravita_conseguenze}
                                onValueChange={(value) => updateFormData('malasanita_gravita_conseguenze', value)}
                                className="space-y-3"
                              >
                                {[
                                  { value: 'danno_lieve_temporaneo', label: 'Danno lieve o temporaneo' },
                                  { value: 'danno_permanente', label: 'Danno permanente' },
                                  { value: 'decesso_paziente', label: 'Decesso del paziente' },
                                  { value: 'non_so_definire', label: 'Non so definire' },
                                ].map((opt) => (
                                  <label
                                    key={opt.value}
                                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
                                    style={{
                                      backgroundColor: formData.malasanita_gravita_conseguenze === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240',
                                      border: `1px solid ${formData.malasanita_gravita_conseguenze === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                                    }}
                                  >
                                    <RadioGroupItem value={opt.value} id={`malasanita-gravita-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Quali documenti medici hai già?
                              </Label>
                              <RadioGroup
                                value={formData.malasanita_documenti_medici}
                                onValueChange={(value) => updateFormData('malasanita_documenti_medici', value)}
                                className="space-y-3"
                              >
                                {[
                                  { value: 'cartella_clinica_completa', label: 'Cartella clinica completa' },
                                  { value: 'referti_ed_esami', label: 'Referti ed esami' },
                                  { value: 'perizia_medico_legale', label: 'Perizia medico-legale' },
                                  { value: 'solo_documenti_sparsi', label: 'Solo documenti sparsi' },
                                  { value: 'nulla', label: 'Nulla' },
                                ].map((opt) => (
                                  <label
                                    key={opt.value}
                                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
                                    style={{
                                      backgroundColor: formData.malasanita_documenti_medici === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240',
                                      border: `1px solid ${formData.malasanita_documenti_medici === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                                    }}
                                  >
                                    <RadioGroupItem value={opt.value} id={`malasanita-documenti-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Cosa è successo dopo il trattamento?
                              </Label>
                              <RadioGroup
                                value={formData.malasanita_esito_post_trattamento}
                                onValueChange={(value) => updateFormData('malasanita_esito_post_trattamento', value)}
                                className="space-y-3"
                              >
                                {[
                                  { value: 'reintervento_peggioramento', label: 'Re-intervento/Peggioramento' },
                                  { value: 'infezione_complicazioni', label: 'Infezione/Complicazioni' },
                                  { value: 'nuovo_ricovero', label: 'Nuovo ricovero' },
                                  { value: 'nessun_peggioramento', label: 'Nessun peggioramento' },
                                ].map((opt) => (
                                  <label
                                    key={opt.value}
                                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
                                    style={{
                                      backgroundColor: formData.malasanita_esito_post_trattamento === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240',
                                      border: `1px solid ${formData.malasanita_esito_post_trattamento === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                                    }}
                                  >
                                    <RadioGroupItem value={opt.value} id={`malasanita-esito-post-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Ha inciso sul lavoro o vita quotidiana?
                              </Label>
                              <RadioGroup
                                value={formData.malasanita_impatto_vita_lavoro}
                                onValueChange={(value) => updateFormData('malasanita_impatto_vita_lavoro', value)}
                                className="space-y-3"
                              >
                                {[
                                  { value: 'incapacita_totale', label: 'Incapacità totale' },
                                  { value: 'incapacita_parziale', label: 'Incapacità parziale' },
                                  { value: 'no', label: 'No' },
                                ].map((opt) => (
                                  <label
                                    key={opt.value}
                                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
                                    style={{
                                      backgroundColor: formData.malasanita_impatto_vita_lavoro === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240',
                                      border: `1px solid ${formData.malasanita_impatto_vita_lavoro === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                                    }}
                                  >
                                    <RadioGroupItem value={opt.value} id={`malasanita-impatto-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Vuoi autorizzare un medico legale partner ad analizzare i dati?
                              </Label>
                              <RadioGroup
                                value={formData.malasanita_autorizzazione_medico_legale}
                                onValueChange={(value) => updateFormData('malasanita_autorizzazione_medico_legale', value)}
                                className="space-y-3"
                              >
                                {[
                                  { value: 'si_autorizzo', label: 'Sì, autorizzo' },
                                  { value: 'no_preferisco_parlarne_prima', label: 'No, preferisco parlarne prima' },
                                ].map((opt) => (
                                  <label
                                    key={opt.value}
                                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
                                    style={{
                                      backgroundColor: formData.malasanita_autorizzazione_medico_legale === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240',
                                      border: `1px solid ${formData.malasanita_autorizzazione_medico_legale === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                                    }}
                                  >
                                    <RadioGroupItem value={opt.value} id={`malasanita-autorizzazione-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                          </>
                        ) : (
                          <>
                            <h3 className="text-xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
                              Dettagli dell&apos;incidente
                            </h3>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Responsabilità
                              </Label>
                              <RadioGroup value={formData.responsabilita} onValueChange={(value) => updateFormData('responsabilita', value)} className="space-y-3">
                                {[
                                  { value: 'ragione_piena', label: 'Ragione piena' },
                                  { value: 'concorso_colpa', label: 'Concorso colpa' },
                                  { value: 'colpa_propria', label: 'Colpa propria' },
                                  { value: 'non_so', label: 'Non so' },
                                ].map((opt) => (
                                  <label key={opt.value} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all" style={{ backgroundColor: formData.responsabilita === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240', border: `1px solid ${formData.responsabilita === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}` }}>
                                    <RadioGroupItem value={opt.value} id={`stradale-responsabilita-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Tipo Incidente
                              </Label>
                              <RadioGroup value={formData.stradale_tipo_incidente} onValueChange={(value) => updateFormData('stradale_tipo_incidente', value)} className="space-y-3">
                                {[
                                  { value: 'tamponamento', label: 'Tamponamento' },
                                  { value: 'scontro_laterale', label: 'Scontro laterale' },
                                  { value: 'frontale', label: 'Frontale' },
                                  { value: 'pedone', label: 'Pedone' },
                                  { value: 'moto', label: 'Moto' },
                                  { value: 'altro', label: 'Altro' },
                                ].map((opt) => (
                                  <label key={opt.value} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all" style={{ backgroundColor: formData.stradale_tipo_incidente === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240', border: `1px solid ${formData.stradale_tipo_incidente === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}` }}>
                                    <RadioGroupItem value={opt.value} id={`stradale-tipo-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Lesioni
                              </Label>
                              <RadioGroup value={formData.stradale_lesioni} onValueChange={(value) => updateFormData('stradale_lesioni', value)} className="space-y-3">
                                {[
                                  { value: 'gravi', label: 'Gravi (intervento/ricovero)' },
                                  { value: 'moderate', label: 'Moderate (prognosi)' },
                                  { value: 'lievi', label: 'Lievi' },
                                  { value: 'nessuna', label: 'Nessuna' },
                                ].map((opt) => (
                                  <label key={opt.value} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all" style={{ backgroundColor: formData.stradale_lesioni === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240', border: `1px solid ${formData.stradale_lesioni === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}` }}>
                                    <RadioGroupItem value={opt.value} id={`stradale-lesioni-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Prognosi
                              </Label>
                              <RadioGroup value={formData.stradale_prognosi} onValueChange={(value) => updateFormData('stradale_prognosi', value)} className="space-y-3">
                                {[
                                  { value: 'gt_30gg', label: '>30 gg' },
                                  { value: '10_30gg', label: '10-30 gg' },
                                  { value: 'lt_10gg', label: '<10 gg' },
                                  { value: 'nessuna', label: 'Nessuna' },
                                ].map((opt) => (
                                  <label key={opt.value} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all" style={{ backgroundColor: formData.stradale_prognosi === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240', border: `1px solid ${formData.stradale_prognosi === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}` }}>
                                    <RadioGroupItem value={opt.value} id={`stradale-prognosi-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Prove
                              </Label>
                              <RadioGroup value={formData.stradale_prove} onValueChange={(value) => updateFormData('stradale_prove', value)} className="space-y-3">
                                {[
                                  { value: 'verbale_polizia', label: 'Verbale Polizia' },
                                  { value: 'testimoni', label: 'Testimoni' },
                                  { value: 'entrambi', label: 'Entrambi' },
                                  { value: 'nessuno', label: 'Nessuno' },
                                ].map((opt) => (
                                  <label key={opt.value} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all" style={{ backgroundColor: formData.stradale_prove === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240', border: `1px solid ${formData.stradale_prove === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}` }}>
                                    <RadioGroupItem value={opt.value} id={`stradale-prove-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Lavoro
                              </Label>
                              <RadioGroup value={formData.stradale_lavoro} onValueChange={(value) => updateFormData('stradale_lavoro', value)} className="space-y-3">
                                {[
                                  { value: 'incapacita_totale', label: 'Incapacità totale' },
                                  { value: 'incapacita_parziale', label: 'Parziale' },
                                  { value: 'no', label: 'No' },
                                ].map((opt) => (
                                  <label key={opt.value} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all" style={{ backgroundColor: formData.stradale_lavoro === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240', border: `1px solid ${formData.stradale_lavoro === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}` }}>
                                    <RadioGroupItem value={opt.value} id={`stradale-lavoro-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                            <div>
                              <Label className="mb-4 block" style={QUESTION_LABEL_STYLE}>
                                Danni Mezzo
                              </Label>
                              <RadioGroup value={formData.stradale_danni_mezzo} onValueChange={(value) => updateFormData('stradale_danni_mezzo', value)} className="space-y-3">
                                {[
                                  { value: 'grave', label: 'Gravemente danneggiato' },
                                  { value: 'medio', label: 'Medi (>1000€)' },
                                  { value: 'lievi', label: 'Lievi' },
                                  { value: 'nessuno', label: 'Nessuno' },
                                ].map((opt) => (
                                  <label key={opt.value} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all" style={{ backgroundColor: formData.stradale_danni_mezzo === opt.value ? 'rgba(212, 175, 55, 0.1)' : '#112240', border: `1px solid ${formData.stradale_danni_mezzo === opt.value ? 'rgba(212, 175, 55, 0.4)' : 'rgba(255,255,255,0.1)'}` }}>
                                    <RadioGroupItem value={opt.value} id={`stradale-danni-${opt.value}`} />
                                    <span style={{ color: '#F8F9FA' }}>{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Step 3: Riepilogo */}
                    {currentStep === 3 && (
                      <div className="animate-fade-in space-y-4" data-testid="form-step-3">
                        <h3 className="text-xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
                          Riepilogo del caso
                        </h3>
                        <p style={{ color: '#A0ABC0' }}>
                          Dettagli pratica completati. Premi Continua per inserire i dati di contatto e inviare la richiesta.
                        </p>
                      </div>
                    )}

                    {/* Step 4: Contatti */}
                    {currentStep === 4 && (
                      <div className="animate-fade-in space-y-4" data-testid="form-step-4">
                        <h3 className="text-xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
                          Come possiamo contattarti?
                        </h3>
                        <div>
                          <Label className="flex items-center gap-2 mb-3" style={QUESTION_LABEL_STYLE}>
                            <User className="w-4 h-4" strokeWidth={1.5} />
                            Nome
                          </Label>
                          <input
                            type="text"
                            placeholder="Mario Rossi"
                            value={formData.nome}
                            onChange={(e) => updateFormData('nome', e.target.value)}
                            className="input-luxury w-full"
                            data-testid="input-nome"
                            style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.1)', color: '#F8F9FA', padding: '10px 12px', borderRadius: 8 }}
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2 mb-3" style={QUESTION_LABEL_STYLE}>
                            <Phone className="w-4 h-4" strokeWidth={1.5} />
                            Telefono
                          </Label>
                          <input
                            type="text"
                            placeholder="+39 333 123 4567"
                            value={formData.telefono}
                            onChange={(e) => updateFormData('telefono', sanitizePhone(e.target.value))}
                            className="input-luxury w-full"
                            data-testid="input-telefono"
                            style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.1)', color: '#F8F9FA', padding: '10px 12px', borderRadius: 8 }}
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2 mb-3" style={QUESTION_LABEL_STYLE}>
                            <Mail className="w-4 h-4" strokeWidth={1.5} />
                            Email
                          </Label>
                          <input
                            type="text"
                            placeholder="mario.rossi@email.com"
                            value={formData.email}
                            onChange={(e) => updateFormData('email', e.target.value)}
                            className="input-luxury w-full"
                            data-testid="input-email"
                            style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.1)', color: '#F8F9FA', padding: '10px 12px', borderRadius: 8 }}
                          />
                        </div>
                      </div>
                    )}

                    {error && (
                      <p className="text-sm mt-4" style={{ color: '#FF4C4C' }}>{error}</p>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex justify-between mt-8">
                      {currentStep > 1 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={prevStep}
                          data-testid="btn-prev-step"
                          style={{ color: '#A0ABC0' }}
                        >
                          Indietro
                        </Button>
                      ) : (
                        <div></div>
                      )}
                      
                      {currentStep < 4 ? (
                        <Button
                          type="button"
                          onClick={nextStep}
                          className={`flex items-center gap-2 ${currentStep === 1 && formData.tipo_incidente ? 'active' : ''}`}
                          data-testid="btn-next-step"
                          style={
                            (currentStep === 1 ? Boolean(formData.tipo_incidente) : isCurrentStepReady)
                              ? { backgroundColor: '#FFD700', color: '#0A192F', opacity: 1, filter: 'none' }
                              : { backgroundColor: '#64748B', color: '#E2E8F0', opacity: 1, filter: 'none' }
                          }
                        >
                          Continua
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      ) : currentStep === 4 ? (
                        <button
                          onClick={handleSubmit}
                          data-testid="btn-submit-lead"
                          style={{
                            backgroundColor: '#0047AB',
                            color: '#FFFFFF',
                            padding: '15px 30px',
                            borderRadius: '8px',
                            fontWeight: 700,
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'background-color 180ms ease-in-out',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#003A8C'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0047AB'; }}
                        >
                          {isSubmitting ? 'Invio...' : 'INVIA RICHIESTA ORA'}
                        </button>
                      ) : (
                        <div />
                      )}
                    </div>
                    {currentStep < 5 && (
                      <div className="mt-4 text-center text-xs sm:text-sm leading-relaxed" style={{ color: '#A0ABC0' }}>
                        <p>Valutazione gratuita e senza impegno.</p>
                        <p>I tuoi dati sono trattati nel rispetto della privacy (GDPR).</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefici */}
      <section className="py-14 px-6" style={{ backgroundColor: '#0A192F' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
              Perché affidarti a LeadLegale
            </h2>
            <p style={{ color: '#A0ABC0' }}>Una gestione professionale del caso, dalla valutazione al risarcimento.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <div className="group p-5 rounded-lg transition-all duration-300 hover:-translate-y-1" style={{ backgroundColor: '#112240', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
              <Wallet className="w-6 h-6 mb-3 transition-transform duration-300 group-hover:scale-110" style={{ color: '#D4AF37' }} strokeWidth={1.75} />
              <h3 className="text-lg font-medium mb-2" style={{ color: '#F8F9FA' }}>Zero Anticipi</h3>
              <p style={{ color: '#A0ABC0' }}>Nessun anticipo richiesto: paghi solo a risultato ottenuto*. *Salvo diversi accordi con lo studio legale partner.</p>
            </div>
            <div className="group p-5 rounded-lg transition-all duration-300 hover:-translate-y-1" style={{ backgroundColor: '#112240', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
              <Stethoscope className="w-6 h-6 mb-3 transition-transform duration-300 group-hover:scale-110" style={{ color: '#D4AF37' }} strokeWidth={1.75} />
              <h3 className="text-lg font-medium mb-2" style={{ color: '#F8F9FA' }}>Medici Esperti</h3>
              <p style={{ color: '#A0ABC0' }}>Valutazioni medico-legali specialistiche per quantificare il danno reale.</p>
            </div>
            <div className="group p-5 rounded-lg transition-all duration-300 hover:-translate-y-1" style={{ backgroundColor: '#112240', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
              <TrendingUp className="w-6 h-6 mb-3 transition-transform duration-300 group-hover:scale-110" style={{ color: '#D4AF37' }} strokeWidth={1.75} />
              <h3 className="text-lg font-medium mb-2" style={{ color: '#F8F9FA' }}>Valutazione Professionale</h3>
              <p style={{ color: '#A0ABC0' }}>Puntiamo ad ottenere il miglior risarcimento previsto dalle tabelle medico-legali vigenti.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Come Funziona */}
      <section className="py-14 px-6" style={{ backgroundColor: '#060F1C' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
              Come Funziona
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <div className="p-5 rounded-lg" style={{ backgroundColor: '#0A192F', border: '1px solid rgba(255,255,255,0.08)' }}>
              <ClipboardCheck className="w-6 h-6 mb-3" style={{ color: '#D4AF37' }} strokeWidth={1.75} />
              <h3 className="text-lg font-medium mb-2" style={{ color: '#F8F9FA' }}>1. Inviaci il caso</h3>
              <p style={{ color: '#A0ABC0' }}>Compila il modulo in pochi minuti con i dettagli principali.</p>
            </div>
            <div className="p-5 rounded-lg" style={{ backgroundColor: '#0A192F', border: '1px solid rgba(255,255,255,0.08)' }}>
              <PhoneCall className="w-6 h-6 mb-3" style={{ color: '#D4AF37' }} strokeWidth={1.75} />
              <h3 className="text-lg font-medium mb-2" style={{ color: '#F8F9FA' }}>2. Valutazione gratuita</h3>
              <p style={{ color: '#A0ABC0' }}>Il team legale analizza documentazione e idoneità senza costi iniziali.</p>
            </div>
            <div className="p-5 rounded-lg" style={{ backgroundColor: '#0A192F', border: '1px solid rgba(255,255,255,0.08)' }}>
              <ShieldCheck className="w-6 h-6 mb-3" style={{ color: '#D4AF37' }} strokeWidth={1.75} />
              <h3 className="text-lg font-medium mb-2" style={{ color: '#F8F9FA' }}>3. Ottieni il Risarcimento</h3>
              <p style={{ color: '#A0ABC0' }}>Ti guidiamo fino alla migliore soluzione risarcitoria possibile.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sezione Fiducia */}
      <section className="py-10 px-6" style={{ backgroundColor: '#0A192F' }}>
        <div className="max-w-5xl mx-auto">
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(212, 175, 55, 0.25)' }}>
            <img
              src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fm=webp&q=80&w=2070"
              alt="Consulenza legale e accordo professionale per infortunistica stradale e risarcimento danni"
              loading="lazy"
              decoding="async"
              className="w-full h-48 sm:h-64 object-cover"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 px-6" style={{ backgroundColor: '#0A192F' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
            Domande Frequenti
          </h2>
          <Accordion type="single" collapsible className="w-full rounded-lg px-4" style={{ backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.08)' }}>
            <AccordionItem value="faq-1" className="border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              <AccordionTrigger className="text-base hover:no-underline" style={{ color: '#F8F9FA' }}>
                Devo pagare qualcosa prima?
              </AccordionTrigger>
              <AccordionContent style={{ color: '#A0ABC0' }}>
                No, la valutazione iniziale è gratuita e non richiede anticipi.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="faq-2" className="border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              <AccordionTrigger className="text-base hover:no-underline" style={{ color: '#F8F9FA' }}>
                In quanto tempo ricevo una risposta?
              </AccordionTrigger>
              <AccordionContent style={{ color: '#A0ABC0' }}>
                Generalmente entro 24 ore lavorative un consulente ti ricontatta.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="faq-3" className="border-b-0">
              <AccordionTrigger className="text-base hover:no-underline" style={{ color: '#F8F9FA' }}>
                Posso inviare il caso anche se non ho tutti i documenti?
              </AccordionTrigger>
              <AccordionContent style={{ color: '#A0ABC0' }}>
                Sì, inviaci comunque le informazioni disponibili: ti indicheremo poi i documenti da integrare.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6" style={{ backgroundColor: '#060F1C', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Scale className="w-6 h-6" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
              <span className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>LeadLegale</span>
            </div>
            <nav className="flex items-center gap-8">
              <a href="#privacy" className="text-sm transition-colors hover:text-[#D4AF37]" style={{ color: '#A0ABC0' }} data-testid="footer-privacy-link">
                Informativa Privacy
              </a>
              <a href="#cookie" className="text-sm transition-colors hover:text-[#D4AF37]" style={{ color: '#A0ABC0' }} data-testid="footer-cookie-link">
                Informativa Cookie
              </a>
              <a href="#disclaimer" className="text-sm transition-colors hover:text-[#D4AF37]" style={{ color: '#A0ABC0' }} data-testid="footer-disclaimer-link">
                Disclaimer Legale
              </a>
            </nav>
          </div>
          <div className="mt-8 pt-8 text-center text-sm" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', color: '#A0ABC0' }}>
            © 2026 LeadLegale. Tutti i diritti riservati.
          </div>
          <div className="mt-4 text-center text-xs sm:text-sm" style={{ color: '#A0ABC0', opacity: 0.95 }}>
            LeadLegale è una piattaforma di orientamento. Il servizio di assistenza è fornito da studi legali e medici partner esterni.
          </div>
          <div className="mt-2 text-center text-xs sm:text-sm font-medium" style={{ color: '#D4AF37' }}>
            Network di legali esperti selezionati
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
